import { CssBaseline, ThemeProvider } from "@mui/material";
import { RouterProvider } from "react-router-dom";
import { useMemo, ReactNode } from "react";
import { SnackbarProvider } from "notistack";
import { getBrandThemes } from "./theme";
import { BrandingProvider, useBranding } from "./branding/BrandingProvider";
import { ModulesProvider } from "./modules/ModulesProvider";
import { DarkModeProvider, useDarkMode } from "./contexts/DarkModeContext";
import { SimpleModeProvider } from "./contexts/SimpleModeContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { router } from "./router";

/**
 * Theme lives above the router: it depends on the brand and dark-mode contexts, not on the
 * current route, and the router owns everything below it.
 */
function ThemedShell({ children }: { children: ReactNode }) {
  const { darkMode } = useDarkMode();
  const brand = useBranding();
  const theme = useMemo(() => {
    const themes = getBrandThemes(brand.key);
    // darkMode is seeded from the brand's default (CerebraLinked = dark) in DarkModeContext,
    // then fully controlled by the Preferences toggle.
    return darkMode ? themes.dark : themes.light;
  }, [darkMode, brand.key]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <BrandingProvider>
        <ModulesProvider>
          <SubscriptionProvider>
            <DarkModeProvider>
              <SimpleModeProvider>
                <SnackbarProvider
                  maxSnack={3}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  autoHideDuration={4000}
                  style={{ zIndex: 9999 }}
                >
                  <ThemedShell>
                    <RouterProvider router={router} />
                  </ThemedShell>
                </SnackbarProvider>
              </SimpleModeProvider>
            </DarkModeProvider>
          </SubscriptionProvider>
        </ModulesProvider>
      </BrandingProvider>
    </LocalizationProvider>
  );
}

export default App;

// TODO TH NEXT: Make a folder of DB scripts to run (based on date)
// - Add the script which adds the welcome survey onboarding action
